// Путь: D:\QR\QR-WEB\dashboard\src\components\Admin\AdminLeads\AdminLeads.jsx
// Название: Admin Leads Management
// Версия: 1.0.0
// Дата: 26.01.2026

import React, { useState, useEffect } from 'react';
import api from '../../../services/api';
import './AdminLeads.css';

const AdminLeads = () => {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ 
    total: 0, 
    direct: 0, 
    referral: 0,
    thisWeek: 0,
    thisMonth: 0
  });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/leads');
      if (res.data.success) {
        setLeads(res.data.leads || []);
        setStats(res.data.stats || {});
      }
    } catch (error) {
      console.error('Load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTypeBadge = (type) => {
    const badges = {
      direct: { label: 'Прямой', class: 'type-direct', icon: '🎁', fee: '€7' },
      referral: { label: 'Реферал', class: 'type-referral', icon: '👥', fee: '€5' }
    };
    const badge = badges[type] || { label: type, class: 'type-default', icon: '👤', fee: '—' };
    return (
      <div className="type-badge-wrapper">
        <span className={`type-badge ${badge.class}`}>{badge.icon} {badge.label}</span>
        <span className="type-fee">{badge.fee}</span>
      </div>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPhone = (phone) => {
    if (!phone) return '—';
    return phone;
  };

  const filteredLeads = leads.filter(lead => {
    // Type filter
    if (filter !== 'all' && lead.type !== filter) return false;
    
    // Search filter
    if (search) {
      const searchLower = search.toLowerCase();
      return (
        lead.first_name?.toLowerCase().includes(searchLower) ||
        lead.last_name?.toLowerCase().includes(searchLower) ||
        lead.email?.toLowerCase().includes(searchLower) ||
        lead.phone?.toLowerCase().includes(searchLower) ||
        lead.campaign_name?.toLowerCase().includes(searchLower) ||
        lead.brand_name?.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  const totalRevenue = (stats.direct || 0) * 7 + (stats.referral || 0) * 5;

  if (loading) {
    return (
      <div className="admin-leads">
        <div className="admin-loading">⏳ Загрузка...</div>
      </div>
    );
  }

  return (
    <div className="admin-leads">
      <div className="admin-page-header">
        <h1>👥 Лиды</h1>
        <p>Все сгенерированные лиды платформы</p>
      </div>

      {/* Stats Cards */}
      <div className="admin-stats-grid">
        <div className="admin-stat-card">
          <div className="stat-icon">👥</div>
          <div className="stat-info">
            <span className="stat-value">{stats.total}</span>
            <span className="stat-label">Всего лидов</span>
          </div>
        </div>
        <div className="admin-stat-card warning">
          <div className="stat-icon">🎁</div>
          <div className="stat-info">
            <span className="stat-value">{stats.direct}</span>
            <span className="stat-label">Прямых (€7)</span>
          </div>
        </div>
        <div className="admin-stat-card info">
          <div className="stat-icon">👥</div>
          <div className="stat-info">
            <span className="stat-value">{stats.referral}</span>
            <span className="stat-label">Рефералов (€5)</span>
          </div>
        </div>
        <div className="admin-stat-card success">
          <div className="stat-icon">💰</div>
          <div className="stat-info">
            <span className="stat-value">€{totalRevenue}</span>
            <span className="stat-label">Комиссии</span>
          </div>
        </div>
      </div>

      {/* Period Stats */}
      <div className="period-stats-row">
        <div className="period-stat-card">
          <div className="period-icon">📅</div>
          <div className="period-info">
            <span className="period-value">{stats.thisWeek || 0}</span>
            <span className="period-label">За неделю</span>
          </div>
        </div>
        <div className="period-stat-card">
          <div className="period-icon">📆</div>
          <div className="period-info">
            <span className="period-value">{stats.thisMonth || 0}</span>
            <span className="period-label">За месяц</span>
          </div>
        </div>
        <div className="period-stat-card highlight">
          <div className="period-icon">📊</div>
          <div className="period-info">
            <span className="period-value">
              {stats.total > 0 ? ((stats.referral / stats.total) * 100).toFixed(0) : 0}%
            </span>
            <span className="period-label">Виральность</span>
          </div>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="admin-toolbar">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Поиск по имени, email, телефону, бренду..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="admin-filters">
          <button 
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            Все ({leads.length})
          </button>
          <button 
            className={`filter-btn direct ${filter === 'direct' ? 'active' : ''}`}
            onClick={() => setFilter('direct')}
          >
            🎁 Прямые
          </button>
          <button 
            className={`filter-btn referral ${filter === 'referral' ? 'active' : ''}`}
            onClick={() => setFilter('referral')}
          >
            👥 Рефералы
          </button>
        </div>
      </div>

      {/* Leads Table */}
      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Контакт</th>
              <th>Email / Телефон</th>
              <th>Кампания / Бренд</th>
              <th>Тип</th>
              <th>Промокод</th>
              <th>Дата</th>
            </tr>
          </thead>
          <tbody>
            {filteredLeads.length === 0 ? (
              <tr>
                <td colSpan="7" className="empty-row">Нет лидов</td>
              </tr>
            ) : (
              filteredLeads.map(lead => (
                <tr key={lead.id}>
                  <td>#{lead.id}</td>
                  <td>
                    <div className="contact-cell">
                      <span className="contact-name">
                        {lead.first_name || ''} {lead.last_name || ''}
                      </span>
                      {lead.email_verified && (
                        <span className="verified-badge" title="Email подтверждён">✅</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="contact-info-cell">
                      <span className="contact-email">{lead.email || '—'}</span>
                      <span className="contact-phone">{formatPhone(lead.phone)}</span>
                    </div>
                  </td>
                  <td>
                    <div className="campaign-cell">
                      <span className="campaign-name">{lead.campaign_name || '—'}</span>
                      <span className="brand-name">{lead.brand_name || '—'}</span>
                    </div>
                  </td>
                  <td>{getTypeBadge(lead.type)}</td>
                  <td>
                    {lead.promocode ? (
                      <code className="promo-code">{lead.promocode}</code>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td className="text-muted">{formatDate(lead.created_at)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminLeads;
